import type { PageSnapshot } from '@travel-planner/shared/schemas';
import { useAuthData } from '@auth/features/auth';
import { useLocation } from '@tanstack/react-router';
import { useMemo } from 'react';

// The page-supplied part of the snapshot. `route` and `permissions` are filled
// in by the hook from the router + the auth context, so a page only declares
// what it knows: its filters, visible columns, selection, and per-page extras.
export type AgentContextInput = {
  currentFilters?: PageSnapshot['currentFilters'];
  visibleColumns?: PageSnapshot['visibleColumns'];
  selectedRowIds?: PageSnapshot['selectedRowIds'];
  extras?: PageSnapshot['extras'];
  // Override the route (defaults to the current pathname). Rarely needed.
  route?: string;
};

// Compose the live page snapshot (#274) sent to the dynamic-agent loop. Reads
// the current route from the router and the caller's permissions from auth,
// then folds in the page-supplied partial. Recomputed each render so filters /
// selection are current when `useAgentRun` reads `adapter.snapshot` at call
// time. Wire the result into the page's `AgentPanelAdapter.snapshot`.
//
// NOTE: the returned `permissions` are descriptive only — the server gates
// tools against its own authoritative grant set, never this list.
export const useAgentContext = (input: AgentContextInput): PageSnapshot => {
  const location = useLocation();
  const auth = useAuthData();
  const permissions = auth.data?.user.permissions;
  const route = input.route ?? location.pathname;

  const { currentFilters, visibleColumns, selectedRowIds, extras } = input;

  return useMemo(
    () => ({
      route,
      ...(permissions?.length ? { permissions } : {}),
      ...(currentFilters ? { currentFilters } : {}),
      ...(visibleColumns?.length ? { visibleColumns } : {}),
      ...(selectedRowIds?.length ? { selectedRowIds } : {}),
      ...(extras ? { extras } : {}),
    }),
    [route, permissions, currentFilters, visibleColumns, selectedRowIds, extras]
  );
};
